import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useGetProgramsQuery, useGetUniversitiesQuery, useAddProgramMutation, useUpdateProgramMutation, useDeleteProgramMutation, useGetCurrenciesQuery, useGetEducationLevelsQuery } from '../store/api';
import TableHeader from '../components/TableHeader';
import ExportButton from '../components/ExportButton';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner';
import { usePermission } from '../hooks/useAuth';

export default function UniversityPrograms() {
  // Permission hooks - strictly permission-based
  const canAddUniversityProgram = usePermission('university_program.add');
  const canEditUniversityProgram = usePermission('university_program.edit');
  const canDeleteUniversityProgram = usePermission('university_program.delete');
  const canViewUniversityProgram = usePermission('university_program.view'); // Already being used in route protection
  
  const [searchTerm, setSearchTerm] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  const [degreeFilter, setDegreeFilter] = useState('');
  const [durationFilter, setDurationFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [viewingProgram, setViewingProgram] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [totalPages, setTotalPages] = useState(1);
  const [totalProgramCount, setTotalProgramCount] = useState(0);
  const [programsData, setProgramsData] = useState(null);
  const [isExport, setIsExport] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    university_id: '',
    degree: [],
    duration: '',
    tuition_amount: '',
    currency_id: '',
    description: '',
    is_active: 1
  });

  // Get Programs Query
  const { data, isError, error, isSuccess, isLoading } = useGetProgramsQuery({
    keyword: searchTerm,
    university_id: universityFilter,
    // degree: degreeFilter,
    // duration: durationFilter,
    // currency: currencyFilter,
    is_active: statusFilter,
    is_export: isExport,
    page: currentPage,
    limit: itemsPerPage
  });

  // Get Universities for filter
  const { data: universitiesData } = useGetUniversitiesQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  // Get Currencies for form
  const { data: currenciesData } = useGetCurrenciesQuery();

  // Get Education Levels for form
  const { data: educationLevelsData } = useGetEducationLevelsQuery();

  // Extract actual data arrays from API responses
  const currencies = currenciesData?.data || currenciesData || [];
  const educationLevels = educationLevelsData?.data || educationLevelsData || [];

  // Helper function to get degree names from IDs
  const getDegreeNames = (degreeIds) => {
    if (!degreeIds || !educationLevels) return '';
    
    const ids = typeof degreeIds === 'string' ? 
      degreeIds.split(',').map(id => id.trim()) : 
      degreeIds;
    
    const names = ids.map(id => {
      const level = educationLevels.find(l => l.id && l.id.toString() === id.toString());
      return level ? level.name : id;
    }).filter(Boolean);
    
    return names.join(', ');
  };

  useEffect(() => {
    if (isExport == 1 && data?.data) {
      const transformedData = data.data.map(program => ({
        'İxtisas Adı': program.name || '',
        'Universitet': program.university?.name || '',
        'Dərəcə': getDegreeNames(program.degree),
        'Müddət': program.duration || '',
        'Təhsil Haqqı': `${program.tuition_amount || 0} ${typeof program.currency === 'object' ? 
          (program.currency?.code || program.currency?.name || '') : 
          (program.currency || '')}`,
        'Status': program.is_active ? 'Aktiv' : 'Deaktiv',
        'Təsvir': program.description || '',
        'ID': program.id || ''
      }));

      setExportData(transformedData);
    } else {
      if (!isLoading && data?.data) {
        setProgramsData(data.data.data || []);
        setTotalPages(Math.ceil((data.data.total || 0) / (data.data.per_page || 10)));
        setTotalProgramCount(data.data.total || 0);
      }
    }
  }, [data, isSuccess, isError, isLoading, error]);

  // Add Program Mutation
  const [addProgram, {
    data: createResponse,
    isError: createIsError,
    error: createError,
    isSuccess: createIsSuccess,
    isLoading: createIsLoading
  }] = useAddProgramMutation();

  useEffect(() => {
    if (!createIsLoading && createResponse) {
      toast.success(createResponse.message);
      setIsModalOpen(false);
      setFormData({
        name: '',
        university_id: '',
        degree: [],
        duration: '',
        tuition_amount: '',
        currency_id: '',
        description: '',
        is_active: 1
      });
    }

    if (createIsError) {
      toast.error(createResponse?.message || 'Əlavə etmə əməliyyatı uğursuz oldu');
    }
  }, [createResponse, createIsSuccess, createIsError, createIsLoading, createError]);

  // Update Program Mutation
  const [updateProgram, {
    data: updateData,
    isError: updateIsError,
    error: updateError,
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading
  }] = useUpdateProgramMutation();

  useEffect(() => {
    if (!updateIsLoading && updateData) {
      toast.success(updateData.message);
      setIsModalOpen(false);
      setEditingProgram(null);
      setFormData({
        name: '',
        university_id: '',
        degree: [],
        duration: '',
        tuition_amount: '',
        currency_id: '',
        description: '',
        is_active: 1
      });
    }

    if (updateIsError) {
      toast.error('Yeniləmə əməliyyatı uğursuz oldu');
      console.log(updateError);
    }
  }, [updateData, updateIsSuccess, updateIsError, updateIsLoading, updateError]);

  // Delete Program Mutation
  const [deleteProgram, {
    data: deleteRes,
    isError: deleteIsError,
    error: deleteError,
    isSuccess: deleteIsSuccess,
    isLoading: deleteIsLoading
  }] = useDeleteProgramMutation();

  useEffect(() => {
    if (!deleteIsLoading && deleteRes && deleteIsSuccess) {
      toast.success(deleteRes.message);
    }

    if (deleteIsError && deleteError) {
      toast.error('Silmə əməliyyatı uğursuz oldu');
    }
  }, [deleteIsSuccess, deleteIsError, deleteRes, deleteError, deleteIsLoading]);

  // Get filtered programs from API data
  const filteredPrograms = programsData || [];
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Export data preparation
  const exportHeaders = ['İxtisas Adı', 'Universitet', 'Dərəcə', 'Müddət', 'Təhsil Haqqı', 'Status', 'Təsvir', 'ID'];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setEditingProgram(null);
    setFormData({
      name: '',
      university_id: '',
      degree: [],
      duration: '',
      tuition_amount: '',
      currency_id: '',
      description: '',
      is_active: 1
    });
    setIsModalOpen(true);
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    
    // Handle degree - convert comma-separated string to array
    let degreeArray = [];
    if (program.degree) {
      if (typeof program.degree === 'string') {
        degreeArray = program.degree.split(',').map(id => id.trim()).filter(Boolean);
      } else if (Array.isArray(program.degree)) {
        degreeArray = program.degree;
      }
    }
    
    setFormData({
      name: program.name,
      university_id: program.university_id?.toString() || '',
      degree: degreeArray,
      duration: program.duration,
      tuition_amount: program.tuition_amount,
      currency_id: program.currency_id || (typeof program.currency === 'object' ? program.currency?.id?.toString() : ''),
      description: program.description || '',
      is_active: program.is_active ? 1 : 0
    });
    setIsModalOpen(true);
  };

  const handleView = (program) => {
    setViewingProgram(program);
    setActiveTab('details');
    setIsViewModalOpen(true);
  };

  const handleDelete = (programId, programName) => {
    if (window.confirm(`"${programName}" ixtisasını silmək istədiyinizə əminsiniz?`)) {
      deleteProgram(programId);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setUniversityFilter('');
    setStatusFilter('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const programData = {
      ...formData,
      university_id: parseInt(formData.university_id) || null,
      tuition_amount: parseFloat(formData.tuition_amount) || null,
      currency_id: parseInt(formData.currency_id) || null,
      degree: Array.isArray(formData.degree) ? formData.degree.join(',') : formData.degree,
      is_active: formData.is_active ? 1 : 0
    };

    if (editingProgram) {
      updateProgram({ id: editingProgram.id, obj: programData });
    } else {
      addProgram(programData);
    }
  };

  // Helper functions
  const getUniversityDetails = (universityName) => {
    // Find university from the API data
    const universities = universitiesData?.data?.data || [];
    return universities.find(uni => uni.name === universityName);
  };

  const getProgramApplications = (programId) => {
    // Placeholder for program applications - would need real API call
    return [];
  };

  // Note: University options are now directly from API data

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ixtisaslar</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ixtisasları idarə edin
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {totalProgramCount}
            </span>
            {(searchTerm || universityFilter || statusFilter) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: {filteredPrograms.length}
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0">
          {canAddUniversityProgram && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni İxtisas
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* First Row - Search and Main Filters */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="İxtisas, universitet, təsvir ilə axtarış..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div style={{width:'300px'}}>
            <Select
              value={universityFilter ? {
                value: universityFilter,
                label: universitiesData?.data?.data?.find(uni => uni.id.toString() === universityFilter)?.name || universityFilter
              } : null}
              onChange={(selectedOption) => setUniversityFilter(selectedOption?.value || '')}
              options={universitiesData?.data?.data ? universitiesData.data.data.map(university => ({
                value: university.id.toString(),
                label: university.name
              })) : []}
              placeholder="Bütün universitetlər"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          {/* <div>
            <Select
              value={degreeFilter ? {
                value: degreeFilter,
                label: degreeFilter
              } : null}
              onChange={(selectedOption) => setDegreeFilter(selectedOption?.value || '')}
              options={uniqueDegrees.map(degree => ({
                value: degree,
                label: degree
              }))}
              placeholder="Bütün dərəcələr"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div> */}
          {/* <div>
            <Select
              value={durationFilter ? {
                value: durationFilter,
                label: durationFilter
              } : null}
              onChange={(selectedOption) => setDurationFilter(selectedOption?.value || '')}
              options={uniqueDurations.map(duration => ({
                value: duration,
                label: duration
              }))}
              placeholder="Bütün müddətlər"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div> */}
        </div>

        {/* Second Row - Currency, Status Filter and Clear Button */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* <div>
            <Select
              value={currencyFilter ? {
                value: currencyFilter,
                label: currencyFilter
              } : null}
              onChange={(selectedOption) => setCurrencyFilter(selectedOption?.value || '')}
              options={uniqueCurrencies.map(currency => ({
                value: currency,
                label: currency
              }))}
              placeholder="Bütün valyutalar"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div> */}
          <div>
            <Select
              value={statusFilter ? {
                value: statusFilter,
                label: statusFilter === '1' ? 'Yalnız aktiv' : statusFilter === '0' ? 'Yalnız deaktiv' : 'Status'
              } : null}
              onChange={(selectedOption) => setStatusFilter(selectedOption?.value || '')}
              options={[
                { value: '1', label: 'Yalnız aktiv' },
                { value: '0', label: 'Yalnız deaktiv' }
              ]}
              placeholder="Bütün statuslar"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          <div>
            <button
              onClick={handleClearFilters}
              disabled={!searchTerm && !universityFilter && !statusFilter}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed h-[42px] flex items-center justify-center"
            >
              Filtri təmizlə
            </button>
          </div>
          <div className="md:col-span-3">
            {/* Empty space for better alignment */}
          </div>
        </div>
      </div>

      <TableHeader
        currentPage={currentPage}
        setItemsPerPage={setItemsPerPage}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        startIndex={startIndex}
        title={"İxtisaslar"}
        totalPages={totalPages}
        totalRecordCount={totalProgramCount}
      />

      {/* Programs table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">

        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>İxtisas</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Universitet</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Dərəcə</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Müddət</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Təhsil Haqqı</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Status</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <Audio
                          height="50"
                          width="50"
                          color="#3b82f6"
                          ariaLabel="loading"
                        />
                      </div>
                      <div className="mt-2 text-sm text-gray-500">İxtisas yüklənir...</div>
                    </td>
                  </tr>
                ) : filteredPrograms.map((program, index) => (
                  <tr key={program.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-3 w-3 rounded-full mr-3 transition-colors ${
                          program.is_active ? 'bg-blue-600 group-hover:bg-blue-700' : 'bg-gray-400 group-hover:bg-gray-500'
                        }`}></div>
                        <div>
                          <div className={`text-sm font-semibold transition-colors ${
                            program.is_active ? 'text-gray-900 group-hover:text-blue-900' : 'text-gray-500 group-hover:text-gray-600'
                          }`}>
                            {program.name}
                          </div>
                          <div className="text-xs text-gray-500 max-w-48 truncate" title={program.description}>
                            {program.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {program.university?.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {getDegreeNames(program.degree)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-md border border-blue-100">
                        {program.duration}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="bg-blue-50 px-3 py-1 rounded-md border border-blue-100">
                        <div className="text-sm font-semibold text-gray-900">
                          {program.tuition_amount} {typeof program.currency === 'object' ? 
                            (program.currency?.code || program.currency?.name || '') : 
                            (program.currency || '')}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">İllik</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        program.is_active 
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {program.is_active ? 'Aktiv' : 'Deaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        {canViewUniversityProgram && (
                          <button
                            onClick={() => handleView(program)}
                            className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Ətraflı bax"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEditUniversityProgram && (
                          <button
                            onClick={() => handleEdit(program)}
                            className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Redaktə et"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteUniversityProgram && (
                          <button
                            onClick={() => handleDelete(program.id, program.name)}
                            className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Sil"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isLoading && filteredPrograms.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">İxtisas tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni ixtisas əlavə edin.
              </p>
              {canAddUniversityProgram && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk İxtisası Əlavə Et
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Modal */}
      {isViewModalOpen && viewingProgram && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsViewModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`h-3 w-3 rounded-full ${viewingProgram.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {viewingProgram.name} - Ətraflı Məlumat
                  </h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    {getDegreeNames(viewingProgram.degree)}
                  </span>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    İxtisas Təfərrüatları
                  </button>
                  <button
                    onClick={() => setActiveTab('applications')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'applications'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Müraciətlər ({getProgramApplications(viewingProgram.id).length})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-64">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Program Information Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900">İxtisas Məlumatları</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">İxtisas Adı</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingProgram.name}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Dərəcə</div>
                          <div className="text-sm font-semibold text-gray-900">{getDegreeNames(viewingProgram.degree)}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Müddət</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingProgram.duration}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Təhsil Haqqı</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {viewingProgram.tuition_amount} {typeof viewingProgram.currency === 'object' ? 
                              (viewingProgram.currency?.code || viewingProgram.currency?.name || '') : 
                              (viewingProgram.currency || '')}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">İllik</div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">İxtisas Təsviri</div>
                          <div className="text-sm text-gray-900">{viewingProgram.description}</div>
                        </div>
                      </div>
                    </div>

                    {/* University Information Card */}
                    {viewingProgram.university && (
                      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="h-8 w-8 bg-green-600 rounded-lg flex items-center justify-center">
                            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-semibold text-green-900">Universitet Məlumatları</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="bg-white p-4 rounded-lg border border-green-100">
                            <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Universitet Adı</div>
                            <div className="text-sm font-semibold text-gray-900">{viewingProgram.university?.name}</div>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-green-100">
                            <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Ölkə</div>
                            <div className="text-sm font-semibold text-gray-900">{viewingProgram.university?.country?.name || viewingProgram.university?.country || 'N/A'}</div>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-green-100">
                            <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Şəhər</div>
                            <div className="text-sm font-semibold text-gray-900">{viewingProgram.university?.city?.name || viewingProgram.university?.city || 'N/A'}</div>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-green-100">
                            <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Reytinq</div>
                            <div className="text-sm font-semibold text-gray-900">#{viewingProgram.university?.ranking || 'N/A'}</div>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-green-100">
                            <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Email</div>
                            <div className="text-sm text-gray-900">
                              {viewingProgram.university?.email ? (
                                <a href={`mailto:${viewingProgram.university.email}`} className="text-green-600 hover:underline">
                                  {viewingProgram.university.email}
                                </a>
                              ) : 'N/A'}
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg border border-green-100">
                            <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Vebsayt</div>
                            <div className="text-sm text-gray-900">
                              {viewingProgram.university?.website ? (
                                <a href={viewingProgram.university.website} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline truncate block">
                                  {viewingProgram.university.website?.replace('https://', '')}
                                </a>
                              ) : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'applications' && (
                  <div className="space-y-3">
                    {getProgramApplications(viewingProgram.id).length > 0 ? (
                      getProgramApplications(viewingProgram.id).map((student) => (
                        <div key={student.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">
                                  {student.firstName[0]}{student.lastName[0]}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <div className="font-medium text-gray-900">
                                    {student.firstName} {student.lastName}
                                  </div>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    student.statusId === 6 ? 'bg-green-100 text-green-800 border border-green-200' :
                                    student.statusId === 4 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                    student.statusId === 3 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                    'bg-gray-100 text-gray-800 border border-gray-200'
                                  }`}>
                                    {student.status}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                  <span>📞 {student.phone}</span>
                                  <span>🎓 {student.specialization}</span>
                                  <span>📊 GPA: {student.gpa}</span>
                                  <span>🌍 {student.languageExam}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  <strong>Konsultant:</strong> {student.consultant} | 
                                  <strong> Müraciət tarixi:</strong> {student.firstContactDate} |
                                  <strong> Mənbə:</strong> {student.source}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">
                                Maliyyə proqnozu
                              </div>
                              <div className="text-sm font-medium text-gray-900">
                                {student.financialForecast}
                              </div>
                              <div className="text-xs text-purple-600 mt-1">
                                {student.tuitionFeeRange}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="mx-auto h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                          </svg>
                        </div>
                        <h5 className="text-lg font-medium text-gray-900 mb-2">Müraciət tapılmadı</h5>
                        <p className="text-gray-500">Bu ixtisasa hələ heç bir müraciət yoxdur</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {canEditUniversityProgram && (
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleEdit(viewingProgram);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                    Redaktə Et
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingProgram ? 'İxtisası Redaktə Et' : 'Yeni İxtisas Əlavə Et'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {editingProgram ? 'İxtisas məlumatlarını yeniləyin' : 'Yeni ixtisas məlumatlarını daxil edin'}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      İxtisas Adı *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Məsələn: Computer Science"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Universitet *
                    </label>
                    <select
                      required
                      value={formData.university_id}
                      onChange={(e) => {
                        const selectedUni = universitiesData?.data?.data.find(uni => uni.id === parseInt(e.target.value));
                        setFormData({ 
                          ...formData, 
                          university_id: e.target.value,
                          university: selectedUni ? selectedUni.name : ''
                        });
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Universitet seçin</option>
                      {universitiesData?.data?.data.map(university => (
                        <option key={university.id} value={university.id}>{university.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dərəcə *
                    </label>
                    <Select
                      value={educationLevels && formData.degree && Array.isArray(formData.degree) ? 
                        formData.degree.map(levelId => {
                          const level = educationLevels.find(l => l.id && l.id.toString() === levelId.toString());
                          return level ? { value: level.id.toString(), label: level.name } : null;
                        }).filter(Boolean) : []
                      }
                      onChange={(selectedOptions) => {
                        const selectedIds = selectedOptions && Array.isArray(selectedOptions) ? 
                          selectedOptions.map(option => option.value) : [];
                        setFormData({ ...formData, degree: selectedIds });
                      }}
                      options={educationLevels && Array.isArray(educationLevels) ? 
                        educationLevels.map(level => ({
                          value: level.id ? level.id.toString() : '',
                          label: level.name || ''
                        })) : []}
                      placeholder="Dərəcə seçin..."
                      isSearchable={true}
                      isMulti={true}
                      isClearable={true}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                          boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                          '&:hover': {
                            borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                          }
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected 
                            ? '#3b82f6' 
                            : state.isFocused 
                            ? '#dbeafe' 
                            : 'white',
                          color: state.isSelected ? 'white' : '#374151'
                        })
                      }}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Müddət *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Məsələn: 2 il"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Təhsil Haqqı *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.tuition_amount}
                      onChange={(e) => setFormData({ ...formData, tuition_amount: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Məsələn: 8000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valyuta *
                    </label>
                    <Select
                      value={currencies && formData.currency_id ? 
                        (() => {
                          const foundCurrency = currencies.find(curr => curr.id && curr.id.toString() === formData.currency_id.toString());
                          return foundCurrency ? 
                            { value: foundCurrency.id.toString(), label: `${foundCurrency.code} - ${foundCurrency.name}` } : 
                            null;
                        })()
                        : null}
                      onChange={(selectedOption) => {
                        setFormData({ ...formData, currency_id: selectedOption?.value || '' });
                      }}
                      options={currencies ? currencies.map(currency => ({
                        value: currency.id.toString(),
                        label: `${currency.code} - ${currency.name}`
                      })) : []}
                      placeholder="Valyuta seçin və ya axtarın..."
                      isSearchable={true}
                      isClearable={true}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                          boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                          '&:hover': {
                            borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                          }
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected 
                            ? '#3b82f6' 
                            : state.isFocused 
                            ? '#dbeafe' 
                            : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#3b82f6' : '#dbeafe'
                          }
                        })
                      }}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.is_active === 1 || formData.is_active === true}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        İxtisas aktiv statusdadır
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İxtisas Təsviri
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="İxtisas haqqında qısa məlumat..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Ləğv et
                  </button>
                  {(editingProgram ? canEditUniversityProgram : canAddUniversityProgram) && (
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {editingProgram ? 'Yenilə' : 'Yadda saxla'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 